import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  IProduct,
  IProductSelectorTemplate,
  ISelectorNode,
  ISelectorOption,
  ISelectorValidationResult,
} from '@topshots/core-models';
import { useGetProductsQuery } from '../products/products.api';
import {
  useDeleteSelectorTemplateMutation,
  useGetSelectorTemplateQuery,
  useUpdateSelectorTemplateMutation,
} from './product-selectors.api';
import Routes from '../../app/routes';
import {
  createNodeId,
  createOptionId,
  normalizeOptions,
  sortOptions,
  validateTree,
} from './selector-editor-utils';

export interface SelectorTemplateDraft {
  id: string;
  draft: IProductSelectorTemplate | null;
  isLoading: boolean;
  nodes: ISelectorNode[];
  productsById: Map<string, IProduct>;
  productOptions: IProduct[];
  uncoveredProducts: IProduct[];
  validation: ISelectorValidationResult;
  updateDraft: (
    updater: (
      current: IProductSelectorTemplate
    ) => IProductSelectorTemplate
  ) => void;
  updateNode: (
    nodeId: string,
    updater: (current: ISelectorNode) => ISelectorNode
  ) => void;
  updateOption: (
    nodeId: string,
    optionId: string,
    updater: (current: ISelectorOption) => ISelectorOption
  ) => void;
  handleAddNode: () => string;
  handleRemoveNode: (nodeId: string) => void;
  handleAddOption: (nodeId: string) => void;
  handleRemoveOption: (nodeId: string, optionId: string) => void;
  handleMoveOption: (
    nodeId: string,
    sourceId: string,
    targetId: string
  ) => void;
  handleSave: () => Promise<void>;
  handleArchive: () => Promise<void>;
}

export const useSelectorTemplateDraft = (): SelectorTemplateDraft => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useGetSelectorTemplateQuery(id as string);
  const [updateTemplate] = useUpdateSelectorTemplateMutation();
  const [deleteTemplate] = useDeleteSelectorTemplateMutation();

  const { data: productsData } = useGetProductsQuery({
    page: 1,
    perPage: 1000,
    includeArchived: false,
    includeUnconfirmed: false,
  });

  const productsById = useMemo(() => {
    const entries: Array<[string, IProduct]> = (
      productsData?.data ?? []
    ).map((product) => [product._id.toString(), product]);
    return new Map<string, IProduct>(entries);
  }, [productsData]);

  const [draft, setDraft] = useState<IProductSelectorTemplate | null>(null);

  useEffect(() => {
    if (data) {
      setDraft(data);
    }
  }, [data]);

  const validation = useMemo(() => {
    if (!draft) {
      return { valid: false, errors: [], warnings: [] };
    }
    return validateTree(draft.tree, productsById);
  }, [draft, productsById]);

  const nodes = useMemo(() => {
    if (!draft) return [];
    return Object.values(draft.tree.nodes).sort((a, b) => {
      if (a.id === draft.tree.rootNodeId) return -1;
      if (b.id === draft.tree.rootNodeId) return 1;
      return a.id.localeCompare(b.id);
    });
  }, [draft]);

  const uncoveredProducts = useMemo(() => {
    if (!draft) return [];
    const referenced = new Set<string>();
    for (const node of Object.values(draft.tree.nodes)) {
      for (const option of node.options) {
        if (option.next.type === 'product') {
          referenced.add(option.next.productId);
        }
      }
    }

    return (productsData?.data ?? [])
      .filter((product) => {
        const productId = product._id.toString();
        return (
          product.isPublic &&
          !product.isArchived &&
          !product.addOnFor &&
          !referenced.has(productId)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [draft, productsData]);

  const updateDraft = (
    updater: (
      current: IProductSelectorTemplate
    ) => IProductSelectorTemplate
  ) => {
    setDraft((current) => (current ? updater(current) : current));
  };

  const updateNode = (
    nodeId: string,
    updater: (current: ISelectorNode) => ISelectorNode
  ) => {
    updateDraft((current) => ({
      ...current,
      tree: {
        ...current.tree,
        nodes: {
          ...current.tree.nodes,
          [nodeId]: updater(current.tree.nodes[nodeId]),
        },
      },
    }));
  };

  const updateOption = (
    nodeId: string,
    optionId: string,
    updater: (current: ISelectorOption) => ISelectorOption
  ) => {
    updateNode(nodeId, (node) => ({
      ...node,
      options: node.options.map((option) =>
        option.id === optionId ? updater(option) : option
      ),
    }));
  };

  const handleAddNode = (): string => {
    const newId = createNodeId(draft?.tree.nodes ?? {});
    const newNode: ISelectorNode = {
      id: newId,
      prompt: 'New question',
      options: [],
    };

    updateDraft((current) => ({
      ...current,
      tree: {
        ...current.tree,
        nodes: {
          ...current.tree.nodes,
          [newId]: newNode,
        },
      },
    }));

    return newId;
  };

  const handleRemoveNode = (nodeId: string) => {
    if (!draft || Object.keys(draft.tree.nodes).length === 1) {
      toast.error('A template must have at least one node.');
      return;
    }

    updateDraft((current) => {
      const { [nodeId]: removed, ...remaining } = current.tree.nodes;
      const remainingNodeIds = Object.keys(remaining);
      const nextRoot =
        current.tree.rootNodeId === nodeId
          ? remainingNodeIds[0]
          : current.tree.rootNodeId;

      return {
        ...current,
        tree: {
          rootNodeId: nextRoot,
          nodes: remaining,
        },
      };
    });
  };

  const handleAddOption = (nodeId: string) => {
    updateNode(nodeId, (node) => {
      const optionId = createOptionId(node.options);
      const newOption: ISelectorOption = {
        id: optionId,
        label: 'New option',
        next: { type: 'none' },
        sortOrder: node.options.length,
      };

      return {
        ...node,
        options: normalizeOptions([...node.options, newOption]),
      };
    });
  };

  const handleRemoveOption = (nodeId: string, optionId: string) => {
    updateNode(nodeId, (node) => ({
      ...node,
      options: normalizeOptions(
        node.options.filter((option) => option.id !== optionId)
      ),
    }));
  };

  const handleMoveOption = (
    nodeId: string,
    sourceId: string,
    targetId: string
  ) => {
    if (sourceId === targetId) return;
    updateNode(nodeId, (node) => {
      const ordered = sortOptions(node.options);
      const sourceIndex = ordered.findIndex(
        (option) => option.id === sourceId
      );
      const targetIndex = ordered.findIndex(
        (option) => option.id === targetId
      );

      if (sourceIndex === -1 || targetIndex === -1) {
        return node;
      }

      const updated = [...ordered];
      const [moved] = updated.splice(sourceIndex, 1);
      updated.splice(targetIndex, 0, moved);

      return {
        ...node,
        options: normalizeOptions(updated),
      };
    });
  };

  const handleSave = async () => {
    if (!draft) return;
    await updateTemplate({ id: id as string, data: draft });
    toast.success('Selector template saved!', { autoClose: 2000 });
  };

  const handleArchive = async () => {
    if (
      !window.confirm(
        'Archive this selector template? This cannot be undone.'
      )
    ) {
      return;
    }
    await deleteTemplate(id as string);
    toast.success('Selector template archived', { autoClose: 2000 });
    navigate(Routes.PRODUCT_SELECTORS.link);
  };

  const productOptions = productsData?.data ?? [];

  return {
    id: id as string,
    draft,
    isLoading,
    nodes,
    productsById,
    productOptions,
    uncoveredProducts,
    validation,
    updateDraft,
    updateNode,
    updateOption,
    handleAddNode,
    handleRemoveNode,
    handleAddOption,
    handleRemoveOption,
    handleMoveOption,
    handleSave,
    handleArchive,
  };
};
